import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';

interface JobOpening {
  id: string;
  title: string;
  location: string;
  department: string;
  employment_type: string;
  description: string;
  requirements: string;
  benefits: string;
  is_active: boolean;
  posted_date: string;
  application_deadline: string | null;
}

interface JobFormProps {
  job?: JobOpening | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
}

const jobSchema = z.object({
  title: z.string().min(3, 'Job title must be at least 3 characters'),
  location: z.string().min(2, 'Location is required'),
  department: z.string().optional(),
  employment_type: z.enum(['full_time', 'part_time', 'contract', 'internship']),
  description: z.string().min(50, 'Description must be at least 50 characters'),
  requirements: z.string().optional(),
  benefits: z.string().optional(),
  is_active: z.boolean(),
  application_deadline: z.string().optional(),
});

type JobFormData = z.infer<typeof jobSchema>;

const JobForm: React.FC<JobFormProps> = ({ job, open, onOpenChange, onSave }) => {
  const { t } = useTranslation(['admin', 'careers']);
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<JobFormData>({
    resolver: zodResolver(jobSchema),
    defaultValues: {
      title: job?.title || '',
      location: job?.location || '',
      department: job?.department || '',
      employment_type: job?.employment_type as any || 'full_time',
      description: job?.description || '',
      requirements: job?.requirements || '',
      benefits: job?.benefits || '',
      is_active: job?.is_active ?? true,
      application_deadline: job?.application_deadline || '',
    },
  });

  const onSubmit = async (data: JobFormData) => {
    setIsSubmitting(true);

    try {
      const jobData = {
        title: data.title,
        location: data.location,
        department: data.department || null,
        employment_type: data.employment_type,
        description: data.description,
        requirements: data.requirements || null,
        benefits: data.benefits || null,
        is_active: data.is_active,
        application_deadline: data.application_deadline || null,
      };

      if (job) {
        // Update existing job
        const { error } = await supabase
          .from('job_openings')
          .update(jobData)
          .eq('id', job.id);

        if (error) throw error;

        toast({
          title: t('admin:success'),
          description: t('admin:jobs.updated'),
        });
      } else {
        // Create new job
        const { error } = await supabase
          .from('job_openings')
          .insert(jobData);

        if (error) throw error;

        toast({
          title: t('admin:success'),
          description: t('admin:jobs.created'),
        });
      }

      onSave();
    } catch (error) {
      console.error('Error saving job:', error);
      toast({
        title: t('admin:error'),
        description: job ? t('admin:jobs.updateError') : t('admin:jobs.createError'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {job ? t('admin:jobs.edit') : t('admin:jobs.create')}
          </DialogTitle>
          <DialogDescription>
            {job ? t('admin:jobs.editDescription') : t('admin:jobs.createDescription')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('admin:jobs.form.title')}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={t('admin:jobs.form.titlePlaceholder')} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('admin:jobs.form.location')}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={t('admin:jobs.form.locationPlaceholder')} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="department"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('admin:jobs.form.department')}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={t('admin:jobs.form.departmentPlaceholder')} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="employment_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('admin:jobs.form.employmentType')}</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('admin:jobs.form.selectType')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="full_time">{t('careers:employmentTypes.full_time')}</SelectItem>
                        <SelectItem value="part_time">{t('careers:employmentTypes.part_time')}</SelectItem>
                        <SelectItem value="contract">{t('careers:employmentTypes.contract')}</SelectItem>
                        <SelectItem value="internship">{t('careers:employmentTypes.internship')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin:jobs.form.description')}</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={4}
                      placeholder={t('admin:jobs.form.descriptionPlaceholder')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="requirements"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin:jobs.form.requirements')}</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={4}
                      placeholder={t('admin:jobs.form.requirementsPlaceholder')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="benefits"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin:jobs.form.benefits')}</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={3}
                      placeholder={t('admin:jobs.form.benefitsPlaceholder')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="application_deadline"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin:jobs.form.deadline')}</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">{t('admin:jobs.form.active')}</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      {t('admin:jobs.form.activeDescription')}
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                {t('admin:cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? t('admin:saving') : (job ? t('admin:update') : t('admin:create'))}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default JobForm;